/** @module SiteAdmin/HomePane/util */
define([
	"underscore",
	"csi/constants",
	"csi/enums",
	"csi/locale",
	"csi/sessionConstants",
	"csi/store/_base",
	"csi/store/Names",
	"csi/store/Values",
	"csi/xml",
	"csi/xml/Query",
], function(_, constants, enums, locale, sessionConstants, csiStore, Names, ValuesStore, xml, Query) {

	const ALL_VALUE = "__All__";
	const FOOD = "Food";
	const FORMULATED_MATERIAL = "FormulatedMaterial";

	const STATE_FILTERS = {
		DocumentRevision: ['PROOFING'],
		FoodSupplierItemRevision: ['DRAFT', 'PENDING', 'APPROVED'],
		PurchasedOrder: ['Negotiation', 'Ready', 'Issued'],
		SupplierItemRevision: ['DRAFT', 'PENDING', 'APPROVED']
	};

	const STATE_IDS = {
		FoodSample: "SampleStatus",
		FormulatedMaterialSample: "SampleStatus",
		MaterialSample: "SampleStatus",
		Sample: "SampleStatus"
	};

	const STATE_PATHS = {
		Document: "Child:CurrentRevision",
		FoodSupplierItem: "Child:CurrentRevision",
		Order: "Child:PO",
		SupplierItem: "Child:CurrentRevision"
	};

	function _getStateAttribute(rc) {
		return rc.getAttribute(getStateId(rc), true);
	}

	/**
	 * Return Values store for given
	 * @param {string} paneId
	 * @param {string} contextType
	 * @param {string?} selectedValue
	 * @return {ValuesStore}
	 */
	function getContextValuesStore(paneId, contextType, selectedValue) {
		const {EMPTY_URL, QRY_VALUE_ATTR, QRY_VALUE_PATH} = constants;
		const filterQuery = new Query();
		const inspiration = isInspirationPane(paneId);
		const requestParameters = {
			[QRY_VALUE_ATTR]: xml.ATTR_NAME,
		};

		if (inspiration) {
			// query for non-empty hierarchy nodes along path from SupplierRequest
			const id = `SR${contextType}`;
			filterQuery.Type("SupplierRequest").RefAttr(id, xml.OP_NOT_EQUALS, EMPTY_URL);
			requestParameters[QRY_VALUE_PATH] = `(SupplierRequest)Child:${id}(${contextType})`;
		} else {
			filterQuery.Type(contextType);
		}

		const store = new ValuesStore({
			filterQuery,
			requestParameters,
		});

		// add option to show all results for inspiration panes
		const all = locale.resource("All");

		if (inspiration) {
			store.addSpecialValue(ALL_VALUE, all);
		}

		if (selectedValue) {
			store.addSelectedValue(selectedValue, selectedValue === ALL_VALUE ? all : selectedValue);
		}

		return store;
	}

	/**
	 * Return Values store for given
	 * @param {string} paneId
	 * @param {string} contextType
	 * @return {ValuesStore}
	 */
	function getProductHierarchyValuesStore(paneId, contextType) {
		const formulatedProductType = (paneId.includes("FormulatedMaterial") ? FORMULATED_MATERIAL : FOOD);
		const s = csiStore.searchByType(contextType);
		const q = s.getQuery().Attr("ApplicableProductTypes", xml.OP_EQUALS, "ProductType:" + formulatedProductType, "Child:Subtype/Child:Settings");
		const store = new Names({
					request: s.getRequest(),
					typeNode: contextType
				});
		return store;
	}


	/**
	 * Return class storing state relative to given class
	   * @param {Class} rc - class of results shown in pane
	   * @return {Class}
	 */
	function getStateClass(rc) {
		var path = getStatePath(rc);
		return path ? rc.getPathClass(path, true) : rc;
	}

	/**
	 * Return attribute id that stores class state
	   * @param {Class} rc - class of results shown in pane
	   * @return {string}
	 */
	function getStateId(rc) {
		rc = getStateClass(rc);
		return STATE_IDS[rc.getType()] || "State";
	}

	/**
	 * Return localized name of state attribute for given class
	   * @param {Class} rc - class of results shown in pane
	   * @return {string}
	 */
	function getStateName(rc) {
		return rc ? locale.typeId(getStateClass(rc).getType(), getStateId(rc)) : "";
	}

	/**
	 * Return path used to get from given class to class with state attribute
	   * @param {Class} rc - class of results shown in pane
	   * @return {string}
	 */
	function getStatePath(rc) {
		return STATE_PATHS[rc.getType()];
	}

	/**
	 * Return store with available States for given class by which the view can be filtered.
	   * @param {Class} rc - class of results shown in pane
	   * @param {boolean} required - if true, then empty value will not be added to store
	   * @return {Memory}
	 */
	function getStateStore(rc, required) {
		rc = getStateClass(rc);
		var rca = _getStateAttribute(rc);
		var enumId = rca && rca.getFormat();
		if (enumId) {
			const available = (sessionConstants.isLocalUser || rc.getType() == "DocumentRevision") ? STATE_FILTERS[rc.getType()] : null;

			function _filter(value, name, item) {
				return _.contains(available, xml.enumGetText(value));
			}

			return enums.getStore(enumId, null, available ? _filter : null, required);
		}

		return null;
	}

	/**
	 * Return true if given class has state attribute
	   * @param {Class} rc - class of results shown in pane
	   * @return {boolean}
	 */
	function hasStateEnum(rc) {
		return !!_getStateAttribute(getStateClass(rc));
	}

	/**
	 * Return true if pane shows inspiration suppler requests or quotes
	 * @param {string} paneId
	 * @return {boolean}
	 */
	function isInspirationPane(paneId) {
		return (paneId == "HomeInspirationStyleRequests" || paneId == "HomeInspirationQuotes");
	}

	return {
		CONTEXT_TYPE_NAMES: [
			"Season",
			"Category1",
			"Category2",
			"Collection"
		],
		PROJECT_CONTEXT: 'Project',
		GLOBAL_CONTEXT: 'User',
		DOMAIN: 'DashboardPaneDomain',
		ALL_VALUE,

		getContextValuesStore,
		getProductHierarchyValuesStore,
		getStateClass,
		getStateId,
		getStateName,
		getStatePath,
		getStateStore,
		hasStateEnum,
		isInspirationPane,
	};
});